import { type FormatPrefs, formatMonthDay } from '@trips/shared/format';
import { perkQuantity, perkStatus } from '@trips/shared/perks';
import type { Perk } from '@trips/shared/schema';
import { useState } from 'react';
import { syncNow } from '../../data/sync';
import { api } from '../../lib/api';
import { navigate } from '../../lib/router';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import { Sheet } from '../../ui/Sheet';
import { toast } from '../../ui/Toast';
import s from './Wallet.module.css';

/**
 * One perk's actions: Mark used (one at a time when there are several), Restore, Edit and
 * Delete. They go through the server so each lands in the perk's history, like Claude's.
 */
export function PerkSheet({
  perk,
  today,
  prefs,
  onClose,
}: {
  perk: Perk | null;
  today: string;
  prefs: FormatPrefs;
  onClose: () => void;
}) {
  const [usedFor, setUsedFor] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!perk)
    return (
      <Sheet open={false} onClose={onClose}>
        {null}
      </Sheet>
    );

  const status = perkStatus(perk, today);
  const qty = perkQuantity(perk);
  const run = async (path: string, method: string, body: unknown, done: string) => {
    setBusy(true);
    try {
      await api(`/api/perks/${encodeURIComponent(perk.id)}${path}`, { method, body });
      await syncNow();
      toast(done);
      onClose();
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That needs a connection.', { tone: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const date = (d: string) =>
    `${formatMonthDay(d, prefs)}${d.slice(0, 4) !== today.slice(0, 4) ? `, ${d.slice(0, 4)}` : ''}`;

  return (
    <Sheet open onClose={onClose} title={perk.title}>
      <p className={`${s.sheetFacts} num`}>
        {[
          qty > 1 ? `×${qty}` : null,
          status === 'used' ? 'Used' : status === 'expired' ? 'Expired' : null,
          perk.expiresOn
            ? `${status === 'expired' ? 'Expired' : 'Expires'} ${date(perk.expiresOn)}`
            : 'No expiry',
          perk.usedOn
            ? `Last used ${date(perk.usedOn)}${perk.usedFor ? ` for ${perk.usedFor}` : ''}`
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {status !== 'used' && (
        <>
          <TextField
            label="Used for (optional)"
            value={usedFor}
            onChange={setUsedFor}
            placeholder="Park Hyatt Tokyo, Nov 3–4"
          />
          <Button
            variant="primary"
            icon="check"
            disabled={busy}
            onClick={() =>
              void run(
                '/use',
                'POST',
                { usedFor: usedFor.trim() || undefined },
                qty > 1 ? `Used one, ${qty - 1} left` : 'Marked used',
              )
            }
          >
            {qty > 1 ? 'Mark one used' : 'Mark used'}
          </Button>
        </>
      )}
      {(status === 'used' || perk.uses?.length) && (
        <Button
          variant="line"
          icon="arrows-clockwise"
          disabled={busy}
          onClick={() => void run('/restore', 'POST', undefined, 'Restored')}
        >
          Undo last use
        </Button>
      )}
      <div className={s.sheetRow}>
        <Button
          variant="line"
          icon="pencil-simple"
          onClick={() => {
            onClose();
            navigate(`/wallet/edit/perk/${perk.id}`);
          }}
        >
          Edit
        </Button>
        {confirmDelete ? (
          <Button
            variant="danger"
            icon="trash"
            disabled={busy}
            onClick={() => void run('', 'DELETE', undefined, 'Deleted')}
          >
            Delete for good
          </Button>
        ) : (
          <Button variant="line" icon="trash" onClick={() => setConfirmDelete(true)}>
            Delete
          </Button>
        )}
      </div>
      {confirmDelete && (
        <p className="t-footnote muted">
          Only for one added by mistake. If it was used, mark it used instead.
        </p>
      )}
    </Sheet>
  );
}
